import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Formik } from 'formik'
import * as yup from 'yup'

import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'

import InfoSection from '@/components/ui/sections/InfoSection'
import SelectField from '@/components/formik/fields/SelectField'
import TextField from '@/components/formik/fields/TextField'
import { useIsSA } from '@/hooks/useIsSA'

export type CreateStaffFormValues = {
  _id?: string
  name: string
  surname: string
  cps?: string
  email: string
  speciality?: string
  status: 'PDS' | 'MEDECIN'
}

export type CreateStaffFormProps = {
  initialValues: CreateStaffFormValues
  isSubmitting?: boolean
  onSubmit: (values: CreateStaffFormValues) => void
  onCancel: () => void
}

const createStaffFormValidationSchema = yup.object().shape({
  name: yup.string().required(),
  surname: yup.string().required(),
  cps: yup.string().optional(),
  email: yup.string().required().email(),
  speciality: yup.string().optional(),
  status: yup.string().required(),
})

export function CreateStaffForm(props: CreateStaffFormProps) {
  const { initialValues, isSubmitting = false, onSubmit, onCancel } = props
  const { t } = useTranslation()
  // SEHA (en-SA): the Specialty field only applies to Doctor accounts.
  const isSA = useIsSA()
  const statusOptions = useMemo(
    () => [
      { label: t('field.status.options.doctor.label').toUpperCase(), value: 'MEDECIN' },
      { label: t('field.status.options.pds.label').toUpperCase(), value: 'PDS' },
    ],
    [t]
  )

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit} validationSchema={createStaffFormValidationSchema}>
      {({ handleSubmit, values }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container rowSpacing={3} columnSpacing={10}>
            <Grid item xs={12} md={12}>
              <InfoSection label={t('page.admin.addStaff.form.title')}>
                <Grid container spacing={3} sx={{ mt: '0px' }}>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.lastName.label')} name="surname" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.firstName.label')} name="name" />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth label={t('field.cps.label')} name="cps" />
                  </Grid>
                  {(!isSA || values.status === 'MEDECIN') && (
                    <Grid item xs={6}>
                      <TextField fullWidth label={t('field.speciality.label')} name="speciality" />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField fullWidth label={t('field.email.label')} name="email" />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectField fullWidth label={t('field.status.label')} name="status" options={statusOptions} />
                  </Grid>
                </Grid>
              </InfoSection>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="end" spacing={2}>
                <Button variant="neutral" onClick={() => onCancel()}>
                  {t('common:button.cancel.label')}
                </Button>
                <LoadingButton data-testid="staff-form-submit" disableElevation loading={isSubmitting} type="submit" variant="contained">
                  {t('common:button.add.label')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default CreateStaffForm
